/**
 * Блок «Уведомления в Telegram» в «Моём профиле» (план 155 Ф3).
 *
 * Сотрудник привязывает свой Telegram к боту уведомлений CRM и выбирает, какие
 * виды уведомлений колокольчика туда дублировать (просьба владельца: «пункт с
 * настройками, какие уведомления можно включать или отключать»).
 *
 * Привязка — одноразовая ссылка `t.me/<бот>?start=…` на 15 минут: на телефоне
 * её открывают, с компьютера — сканируют QR. Пока ссылка на экране, статус
 * опрашивается раз в три секунды: человек нажал «Старт» в боте — блок сам
 * переключается в «Привязан».
 *
 * Бот не настроен на сервере — блока нет вовсе (как у тумблера пушей).
 * Все хуки — до первого `return` (красная линия 4).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toDataURL } from 'qrcode';
import { useEffect, useState } from 'react';

import { Button, buttonVariants } from '@/components/ui/button';
import { ToggleSwitch } from '@/components/ui/toggle-switch';
import { ApiError, errorText } from '@/lib/api';
import { formatDateTime, formatTime } from '@/lib/format';
import { useTenantId } from '@/lib/tenant-context';

import {
  getMyTelegram,
  issueTelegramLink,
  sendTelegramTest,
  setTelegramKind,
  unlinkMyTelegram,
  type MyTelegram,
} from '../api/telegram.api';

/** Как часто спрашивать «привязался ли», пока ссылка на экране. */
const LINK_POLL_MS = 3_000;

export function TelegramBlock(): JSX.Element | null {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  const queryKey = ['me', tenantId, 'telegram'] as const;

  const [pendingLink, setPendingLink] = useState<{ url: string; expiresAt: string } | null>(null);
  const [linkExpired, setLinkExpired] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  // Ошибка показывается у того, что её вызвало: у привязки, у кнопок
  // «Прислать тестовое»/«Отвязать» или у переключателей.
  const [error, setError] = useState<{ at: 'link' | 'actions' | 'kinds'; text: string } | null>(
    null,
  );
  const [testSent, setTestSent] = useState(false);

  const status = useQuery({
    queryKey,
    queryFn: getMyTelegram,
    refetchInterval: pendingLink && !linkExpired ? LINK_POLL_MS : false,
  });
  const data = status.data;
  const link = data?.link ?? null;

  const issue = useMutation({
    mutationFn: issueTelegramLink,
    onMutate: () => setError(null),
    onSuccess: (res) => {
      setLinkExpired(false);
      setPendingLink({ url: res.url, expiresAt: res.expires_at });
    },
    onError: (e) => setError({ at: 'link', text: errorText(e) }),
  });
  const unlink = useMutation({
    mutationFn: unlinkMyTelegram,
    onMutate: () => {
      setError(null);
      setTestSent(false);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey }),
    onError: (e) => setError({ at: 'actions', text: errorText(e) }),
  });
  const test = useMutation({
    mutationFn: sendTelegramTest,
    onMutate: () => {
      setError(null);
      setTestSent(false);
    },
    onSuccess: () => setTestSent(true),
    // «Бот остановлен» объясняет плашка, которая появится после обновления
    // статуса, — второй раз то же самое под кнопками не пишем.
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'staff_telegram.bot_blocked') return;
      setError({ at: 'actions', text: errorText(e) });
    },
    // Итог отправки меняет «последнее сообщение» и пометку «бот остановлен».
    onSettled: () => void qc.invalidateQueries({ queryKey }),
  });
  const toggle = useMutation({
    mutationFn: ({ kind, enabled }: { kind: string; enabled: boolean }) =>
      setTelegramKind(kind, enabled),
    onMutate: () => setError(null),
    onSuccess: (next: MyTelegram) => qc.setQueryData(queryKey, next),
    onError: (e) => setError({ at: 'kinds', text: errorText(e) }),
  });

  // QR рисуется локально: адрес с токеном не уходит сторонним сервисам.
  useEffect(() => {
    if (!pendingLink) {
      setQr(null);
      return;
    }
    let cancelled = false;
    void toDataURL(pendingLink.url, { width: 320, margin: 1 }).then((dataUrl) => {
      if (!cancelled) setQr(dataUrl);
    });
    return () => {
      cancelled = true;
    };
  }, [pendingLink]);

  // Срок ссылки вышел — QR и «Открыть в Telegram» прячем: по ним бот ответит
  // «ссылка устарела», а человек решит, что CRM сломана.
  useEffect(() => {
    if (!pendingLink) return;
    const left = Date.parse(pendingLink.expiresAt) - Date.now();
    if (left <= 0) {
      setLinkExpired(true);
      return;
    }
    const timer = setTimeout(() => setLinkExpired(true), left);
    return () => clearTimeout(timer);
  }, [pendingLink]);

  // Привязка состоялась — ссылка и QR больше не нужны.
  useEffect(() => {
    if (link && pendingLink) setPendingLink(null);
  }, [link, pendingLink]);

  if (!data?.configured) return null;

  const blocked = Boolean(link?.blocked_at);
  const who = link?.username ? `@${link.username}` : 'Telegram';
  // Ошибка доставки — только если она новее последней успешной (план §2.9,
  // «Наблюдаемость»): старая ошибка после удачной отправки уже неактуальна.
  // При остановленном боте причину называет плашка ниже.
  const lastError =
    !blocked &&
    link?.last_error &&
    link.last_error_at &&
    (!link.last_sent_at || Date.parse(link.last_error_at) > Date.parse(link.last_sent_at))
      ? { text: link.last_error, at: link.last_error_at }
      : null;
  const errorAt = (at: 'link' | 'actions' | 'kinds'): JSX.Element | null =>
    error?.at === at ? <p className="mt-2 text-sm text-destructive">{error.text}</p> : null;

  return (
    <div className="rounded-card border border-divider-gray p-4" data-testid="telegram-block">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-ink-black">Уведомления в Telegram</div>
          <p className="mt-1 text-sm text-muted-text">
            {link
              ? `Привязан: ${who}`
              : 'Задачи, заявки и напоминания будут приходить в Telegram, даже когда CRM закрыта.'}
          </p>
          {link && !blocked && link.last_sent_at ? (
            <p className="mt-0.5 text-xs text-muted-text">
              Последнее сообщение: {formatDateTime(link.last_sent_at)}
            </p>
          ) : null}
          {lastError ? (
            <p className="mt-0.5 break-words text-xs text-destructive">
              Последняя ошибка доставки: {lastError.text} ({formatDateTime(lastError.at)})
            </p>
          ) : null}
        </div>
        {!link && !pendingLink ? (
          <Button className="shrink-0" disabled={issue.isPending} onClick={() => issue.mutate()}>
            {issue.isPending ? '…' : 'Привязать'}
          </Button>
        ) : null}
      </div>
      {errorAt('link')}

      {!link && pendingLink && linkExpired ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-base bg-whisper-white p-3">
          <p className="text-sm text-ink-black">Ссылка истекла — она действует 15 минут.</p>
          <Button size="sm" disabled={issue.isPending} onClick={() => issue.mutate()}>
            {issue.isPending ? '…' : 'Получить новую'}
          </Button>
        </div>
      ) : null}

      {!link && pendingLink && !linkExpired ? (
        <div className="mt-3 flex flex-col items-center gap-3 rounded-base bg-whisper-white p-3 sm:flex-row sm:items-start">
          {qr ? (
            <img
              src={qr}
              alt="QR-код для привязки Telegram"
              className="h-40 w-40 shrink-0 rounded-base bg-canvas-white"
            />
          ) : (
            <div className="h-40 w-40 shrink-0 rounded-base bg-canvas-white" aria-hidden="true" />
          )}
          <div className="min-w-0 space-y-2 text-sm text-ink-black">
            <p>
              Откройте ссылку на телефоне с Telegram или наведите камеру на код и нажмите в боте
              «Старт».
            </p>
            <a
              href={pendingLink.url}
              target="_blank"
              rel="noreferrer"
              className={buttonVariants({ size: 'sm' })}
            >
              Открыть в Telegram
            </a>
            <p className="text-xs text-muted-text">
              Ссылка одноразовая, действует до {formatTime(pendingLink.expiresAt)}. После привязки
              этот блок обновится сам.
            </p>
          </div>
        </div>
      ) : null}

      {link ? (
        <>
          {blocked ? (
            <p className="mt-3 rounded-base bg-destructive/10 px-3 py-2 text-sm text-destructive">
              Бот остановлен — сообщения не доходят. Откройте бота в Telegram, нажмите «Старт»,
              затем «Прислать тестовое».
            </p>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={test.isPending}
              onClick={() => test.mutate()}
            >
              {test.isPending ? 'Отправляем…' : 'Прислать тестовое'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={unlink.isPending}
              onClick={() => unlink.mutate()}
            >
              Отвязать
            </Button>
          </div>
          {testSent ? (
            <p className="mt-2 text-sm text-ink-black">Отправили — проверьте Telegram.</p>
          ) : null}
          {errorAt('actions')}

          <div className="mt-4 border-t border-divider-gray pt-3">
            <div className="text-sm font-medium text-ink-black">Что присылать в Telegram</div>
            <ul className="mt-1 divide-y divide-divider-gray">
              {data.kinds.map((k) => (
                <li key={k.kind} className="flex items-center justify-between gap-3 py-2">
                  <span className="min-w-0 text-sm text-ink-black">{k.label}</span>
                  <ToggleSwitch
                    label={k.label}
                    checked={k.enabled}
                    disabled={toggle.isPending}
                    onChange={(enabled) => toggle.mutate({ kind: k.kind, enabled })}
                  />
                </li>
              ))}
            </ul>
            <p className="mt-1 text-xs text-muted-text">
              Выключенное остаётся в колокольчике CRM. Имена и телефоны клиентов в Telegram не
              приходят — только что случилось и ссылка.
            </p>
            {errorAt('kinds')}
          </div>
        </>
      ) : null}
    </div>
  );
}
